import { v4 } from "uuid";

import { PERSONAL_DOMAINS, ROLES } from "./constants";
import { FormFields, QuestionnaireResponse } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { TOUR_QUERY_PARAM } from "@/components/Playground/tour/hooks/useTourUrlParam";
import { USER_ONBOARDING_REQUEST_PARAM_QUESTION_KEYS } from "@/queries/onboarding";
import { PaginatedPromptRegistryObjects } from "@/types/prompt-registry";
import type { ListPromptVersions } from "@/types/prompt-version";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export const isPersonalEmail = (email: string) =>
  PERSONAL_DOMAINS.some(domain => email.endsWith(domain));

interface GetOnboardingDestinationUrlParams {
  workspaceId: number;
  promptTemplateId?: number | null;
  userToken: string;
}

export const getOnboardingDestinationUrl = async ({
  workspaceId,
  promptTemplateId,
  userToken,
}: GetOnboardingDestinationUrlParams): Promise<string | null> => {
  if (!workspaceId) {
    return null;
  }

  let resolvedPromptTemplateId = promptTemplateId;

  if (!resolvedPromptTemplateId) {
    try {
      const url = new URL(ENDPOINTS.get_prompt_registry_objects(workspaceId));
      url.searchParams.set("q", "Example: page_title");
      url.searchParams.set("per_page", "10");

      const response = await fetch(url, { headers: jsonAuthHeaders(userToken) });
      const data = (await handleResponse(response)) as PaginatedPromptRegistryObjects;
      const defaultPrompt = data.items.find(prompt => prompt.prompt_name === "Example: page_title");
      resolvedPromptTemplateId = defaultPrompt?.id;
    } catch {
      return null;
    }
  }

  if (!resolvedPromptTemplateId) {
    return null;
  }

  try {
    const versionsUrl = new URL(ENDPOINTS.prompt_versions(workspaceId));
    versionsUrl.searchParams.set("workspace_id", String(workspaceId));
    versionsUrl.searchParams.set("prompt_registry_id", String(resolvedPromptTemplateId));
    versionsUrl.searchParams.set("page", "1");
    versionsUrl.searchParams.set("per_page", "1");

    const response = await fetch(versionsUrl, { headers: jsonAuthHeaders(userToken) });
    const data = (await handleResponse(response)) as ListPromptVersions;
    const latestVersion = data.prompt_versions[0]?.number;

    return latestVersion
      ? `/workspace/${workspaceId}/prompt/${resolvedPromptTemplateId}/version/${latestVersion}/playground/${v4()}?${TOUR_QUERY_PARAM}`
      : null;
  } catch {
    return null;
  }
};

export const mapDataToFormFields = (data?: QuestionnaireResponse[]) => {
  const initialValue: FormFields = {
    you: { role: "prompt_engineer", specific_role: "" },
    company: { company_name: "", company_size: "" },
    product_areas: ["prompts_and_evals"],
    invitee_emails: [],
  };

  return data?.reduce((acc: FormFields, item) => {
    if (item.onboarding_question_key !== USER_ONBOARDING_REQUEST_PARAM_QUESTION_KEYS.ABOUT_USER) {
      return acc;
    }

    const { response } = item;
    const aboutUser = "about_user" in response ? response.about_user : response;
    const { role, company_size: companySize, company_name: companyName } = aboutUser;
    const existingRole = ROLES.find(candidate => candidate.value === role);

    acc.you = existingRole
      ? { role: existingRole.value, specific_role: "" }
      : { role: "other", specific_role: role ?? "" };
    acc.company = {
      company_name: companyName ?? "",
      company_size: companySize ?? "",
    };

    return acc;
  }, initialValue);
};
